import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useSession } from './useSession';
import { FULL_LIST_SIZE } from '../lib/fullList';
import type { UserPick } from '../types';

export const MAX_LIST_SIZE = FULL_LIST_SIZE;

interface UseMyListResult {
  list: UserPick[];
  isLoaded: boolean;
  error: string | null;
  add: (pick: Omit<UserPick, 'position'>) => Promise<void>;
  remove: (placeId: string) => Promise<void>;
  move: (placeId: string, direction: 'up' | 'down') => Promise<void>;
}

interface RawPickRow {
  name: string;
  place_id: string;
  cuisine: string | null;
  location: string | null;
  position: number;
}

function isRawPickRow(value: unknown): value is RawPickRow {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.name === 'string' &&
    typeof v.place_id === 'string' &&
    typeof v.position === 'number'
  );
}

function rawToPick(row: RawPickRow): UserPick {
  return {
    name: row.name,
    placeId: row.place_id,
    cuisine: row.cuisine ?? '',
    location: row.location ?? '',
    position: row.position,
  };
}

function sortByPosition(list: UserPick[]): UserPick[] {
  return list.slice().sort((a, b) => a.position - b.position);
}

export function useMyList(): UseMyListResult {
  const { session, isLoaded: sessionLoaded } = useSession();
  const userId = session?.user.id ?? null;
  const [list, setList] = useState<UserPick[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A synchronous mirror of `list`. Mutations read from this ref rather than
  // the render closure so that back-to-back actions (especially rapid adds)
  // always see the freshest picks — a stale closure would recompute the same
  // `position` for two picks and let a pick slip past the duplicate guard.
  const listRef = useRef<UserPick[]>([]);
  const commitList = useCallback((next: UserPick[]) => {
    listRef.current = next;
    setList(next);
  }, []);

  const fetchRows = useCallback(async () => {
    if (!userId) {
      commitList([]);
      setIsLoaded(true);
      return;
    }
    const { data, error: fetchError } = await supabase
      .from('user_picks')
      .select('name, place_id, cuisine, location, position')
      .eq('user_id', userId)
      .order('position', { ascending: true });
    if (fetchError) {
      // eslint-disable-next-line no-console
      console.error('Failed to load picks', fetchError);
      setError("We couldn't load your list. Please refresh and try again.");
      setIsLoaded(true);
      return;
    }
    const safe = Array.isArray(data) ? data.filter(isRawPickRow).map(rawToPick) : [];
    commitList(safe);
    setIsLoaded(true);
    // Note: we intentionally do NOT clear `error` here. fetchRows runs after
    // every mutation to reconcile; clearing on its success would wipe a
    // mutation error that was just set. Each mutation clears `error` at its
    // own start instead.
  }, [userId, commitList]);

  useEffect(() => {
    if (!sessionLoaded) return;
    void fetchRows();
  }, [sessionLoaded, fetchRows]);

  // Each mutation updates local state optimistically, then sends the
  // mutation to Supabase, then re-syncs from the database. On error the
  // re-sync restores the canonical state, undoing the optimistic update.

  // Mutations run one at a time through this promise chain. Serializing all
  // of them (and reading `listRef` inside each) means quick successive
  // actions can't race: a remove clicked while an add's insert is still in
  // flight waits for it, so positions stay contiguous and nothing is
  // dropped. The catch keeps one unexpected failure from stalling every
  // later mutation.
  const mutationQueue = useRef<Promise<void>>(Promise.resolve());
  const enqueue = useCallback((fn: () => Promise<void>) => {
    const run = mutationQueue.current.then(fn).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('List update failed', err);
      setError('Something went wrong updating your list. Please refresh.');
    });
    mutationQueue.current = run;
    return run;
  }, []);

  const performAdd = useCallback(
    async (pick: Omit<UserPick, 'position'>) => {
      if (!userId) return;
      const current = listRef.current;
      if (current.some((p) => p.placeId === pick.placeId)) {
        setError('That restaurant is already on your list.');
        return;
      }
      if (current.length >= MAX_LIST_SIZE) {
        setError('Your top five is full. Remove one before adding another.');
        return;
      }
      setError(null);

      const nextPosition =
        current.length > 0
          ? Math.max(...current.map((p) => p.position)) + 1
          : 0;
      const optimistic: UserPick = { ...pick, position: nextPosition };
      commitList([...current, optimistic]);

      const { error: insertError } = await supabase.from('user_picks').insert({
        user_id: userId,
        name: pick.name,
        place_id: pick.placeId,
        // Store empty cuisine/location as NULL (matching the nullable schema
        // and the seeded rows) so the homepage's mode() sample fields don't
        // surface empty strings.
        cuisine: pick.cuisine || null,
        location: pick.location || null,
        position: nextPosition,
      });
      if (insertError) {
        // A unique-violation means this place is already saved for the user —
        // typically because the list was edited from another tab/device and
        // our local copy was stale. That's harmless: the re-fetch below pulls
        // the existing row in, so we show the same gentle "already on your
        // list" note rather than a scary failure.
        if (insertError.code === '23505') {
          setError('That restaurant is already on your list.');
        } else {
          // eslint-disable-next-line no-console
          console.error('Failed to add pick', insertError);
          setError("We couldn't add that restaurant. Please try again.");
        }
      }
      await fetchRows();
    },
    [userId, fetchRows, commitList]
  );

  const performRemove = useCallback(
    async (placeId: string) => {
      if (!userId) return;
      setError(null);
      const current = listRef.current;
      const removedPosition = current.find((p) => p.placeId === placeId)?.position;
      commitList(
        current
          .filter((p) => p.placeId !== placeId)
          .map((p, idx) => ({ ...p, position: idx }))
      );

      const { error: deleteError } = await supabase
        .from('user_picks')
        .delete()
        .eq('user_id', userId)
        .eq('place_id', placeId);
      if (deleteError) {
        // eslint-disable-next-line no-console
        console.error('Failed to remove pick', deleteError);
        setError("We couldn't remove that restaurant. Please try again.");
        await fetchRows();
        return;
      }

      // Renumber the remaining picks so positions stay contiguous 0..N-1.
      // The aggregated_picks view averages raw positions; gaps would skew
      // the rank shown on the homepage. Only update rows whose position
      // would change (anything originally above the removed slot).
      if (removedPosition !== undefined) {
        const shifts = current
          .filter((p) => p.placeId !== placeId && p.position > removedPosition)
          .map((p) =>
            supabase
              .from('user_picks')
              .update({ position: p.position - 1 })
              .eq('user_id', userId)
              .eq('place_id', p.placeId)
          );
        const results = await Promise.all(shifts);
        const failed = results.find((r) => r.error);
        if (failed?.error) {
          // eslint-disable-next-line no-console
          console.error('Failed to renumber picks after remove', failed.error);
          setError("We couldn't update your list order. Please refresh.");
        }
      }

      await fetchRows();
    },
    [userId, fetchRows, commitList]
  );

  const performMove = useCallback(
    async (placeId: string, direction: 'up' | 'down') => {
      if (!userId) return;
      const current = listRef.current;
      const i = current.findIndex((p) => p.placeId === placeId);
      if (i === -1) return;
      const j = direction === 'up' ? i - 1 : i + 1;
      if (j < 0 || j >= current.length) return;
      setError(null);

      const a = current[i];
      const b = current[j];
      commitList(
        sortByPosition(
          current.map((p) => {
            if (p.placeId === a.placeId) return { ...p, position: b.position };
            if (p.placeId === b.placeId) return { ...p, position: a.position };
            return p;
          })
        )
      );

      const { error: rpcError } = await supabase.rpc('swap_user_pick_positions', {
        pick_a_place_id: a.placeId,
        pick_b_place_id: b.placeId,
      });
      if (rpcError) {
        // eslint-disable-next-line no-console
        console.error('Failed to reorder picks', rpcError);
        setError("We couldn't reorder your list. Please try again.");
      }
      await fetchRows();
    },
    [userId, fetchRows, commitList]
  );

  const add = useCallback(
    (pick: Omit<UserPick, 'position'>) => enqueue(() => performAdd(pick)),
    [enqueue, performAdd]
  );

  const remove = useCallback(
    (placeId: string) => enqueue(() => performRemove(placeId)),
    [enqueue, performRemove]
  );

  const move = useCallback(
    (placeId: string, direction: 'up' | 'down') =>
      enqueue(() => performMove(placeId, direction)),
    [enqueue, performMove]
  );

  return { list, isLoaded, error, add, remove, move };
}
